import React, { useState, useEffect, useCallback } from 'react';
import { 
  Clock, AlertTriangle, AlertCircle, CheckCircle2, 
  Calendar, User, Building2, RefreshCw, ChevronRight,
  Timer, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import api from '@/services/api';

// Severity colors and icons
const SEVERITY_CONFIG = {
  CRITICAL: { 
    color: 'bg-red-600/20 text-red-400 border-red-600/40', 
    icon: AlertCircle,
    label: 'Critical'
  },
  URGENT: { 
    color: 'bg-orange-600/20 text-orange-400 border-orange-600/40', 
    icon: AlertTriangle,
    label: 'Urgent'
  },
  APPROACHING: { 
    color: 'bg-gold-600/20 text-gold-400 border-gold-600/40', 
    icon: Clock,
    label: 'Approaching'
  },
  NORMAL: { 
    color: 'bg-green-600/20 text-green-400 border-green-600/40', 
    icon: CheckCircle2,
    label: 'Normal'
  }
};

const DEADLINE_TYPE_LABELS = {
  COMPLETION_DATE: 'Deal Completion',
  TRUSTEE_APPOINTMENT: 'Trustee Appointment',
  NOC_SUBMISSION: 'NOC Submission',
  DEPOSIT_PAYMENT: 'Deposit Payment',
  FINAL_PAYMENT: 'Final Payment',
  DOCUMENT_SUBMISSION: 'Document Submission',
  MORTGAGE_APPROVAL: 'Mortgage Approval',
  FORM_F_SIGNING: 'Form F Signing',
  HANDOVER: 'Handover'
};

const formatDaysRemaining = (days) => {
  if (days === null || days === undefined) return 'N/A';
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return '1 day remaining';
  return `${days} days remaining`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// Summary Stats Component
const DeadlineSummaryStats = ({ summary, loading }) => {
  const stats = [
    { label: 'Active', value: summary?.total_active || 0, color: 'text-blue-400' },
    { label: 'Overdue', value: summary?.overdue_count || 0, color: 'text-red-400' },
    { label: 'Critical', value: summary?.critical_count || 0, color: 'text-red-400' },
    { label: 'Urgent', value: summary?.urgent_count || 0, color: 'text-orange-400' },
    { label: 'Approaching', value: summary?.approaching_count || 0, color: 'text-gold-400' },
    { label: 'Completed Today', value: summary?.completed_today || 0, color: 'text-green-400' }
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {stats.map((stat, idx) => (
        <Card key={idx} className="bg-white/5 border-white/10">
          <CardContent className="p-3 text-center">
            <div className={`text-2xl font-bold ${stat.color}`}>
              {loading ? '-' : stat.value}
            </div>
            <div className="text-xs text-gray-400 mt-1">{stat.label}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Single Deadline Card Component
const DeadlineCard = ({ deadline, onComplete, isCompleting }) => {
  const severity = SEVERITY_CONFIG[deadline.severity] || SEVERITY_CONFIG.NORMAL;
  const SeverityIcon = severity.icon;
  
  return (
    <div className="bg-white/5 border border-white/10 rounded-lg p-4 hover:bg-white/8 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="outline" className={severity.color}>
              <SeverityIcon className="w-3 h-3 mr-1" />
              {severity.label}
            </Badge>
            <Badge variant="outline" className="bg-blue-600/20 text-blue-400 border-blue-600/30">
              {DEADLINE_TYPE_LABELS[deadline.deadline_type] || deadline.deadline_type}
            </Badge>
          </div>
          
          {/* Description */}
          <p className="text-white font-medium truncate">{deadline.description}</p>
          
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(deadline.deadline_date)}
            </span>
            <span className={`flex items-center gap-1 ${deadline.days_remaining < 0 ? 'text-red-400 font-medium' : ''}`}>
              <Timer className="w-4 h-4" />
              {formatDaysRemaining(deadline.days_remaining)}
            </span>
            {deadline.responsible_party && (
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {deadline.responsible_party}
              </span>
            )}
          </div>
          
          {/* Deal ID */}
          <div className="mt-2 text-xs text-gray-500">
            Deal: {deadline.deal_id?.substring(0, 8)}...
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex-shrink-0">
          {!deadline.completed && (
            <Button 
              size="sm" 
              variant="outline" 
              className="bg-green-600/20 text-green-400 border-green-600/30 hover:bg-green-600/30"
              onClick={() => onComplete(deadline.deadline_id)}
              disabled={isCompleting}
            >
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Complete
            </Button>
          )}
          {deadline.completed && (
            <Badge className="bg-green-600/20 text-green-400">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Completed
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Deadlines Monitor Component
const DeadlinesMonitor = ({ dealId = null, compact = false }) => {
  const [summary, setSummary] = useState(null);
  const [overdueDeadlines, setOverdueDeadlines] = useState([]);
  const [upcomingDeadlines, setUpcomingDeadlines] = useState([]);
  const [dealDeadlines, setDealDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState(null);
  const [activeView, setActiveView] = useState('upcoming'); // upcoming, overdue, all

  const fetchDeadlines = useCallback(async () => {
    setLoading(true);
    try {
      // If viewing a specific deal, fetch deal deadlines
      if (dealId) {
        const { data } = await api.get(`/api/governance/deadlines/deal/${dealId}`);
        setDealDeadlines(data);
      } else {
        // Fetch summary and both lists
        const [summaryRes, overdueRes, upcomingRes] = await Promise.all([
          api.get('/api/governance/deadlines/summary'),
          api.get('/api/governance/deadlines/overdue'),
          api.get('/api/governance/deadlines/upcoming?days=14')
        ]);
        
        setSummary(summaryRes.data);
        setOverdueDeadlines(overdueRes.data);
        setUpcomingDeadlines(upcomingRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch deadlines:', error);
      toast.error('Failed to load deadlines');
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchDeadlines();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchDeadlines, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDeadlines]);

  const handleComplete = async (deadlineId) => {
    setCompletingId(deadlineId);
    try {
      await api.post(`/api/governance/deadlines/${deadlineId}/complete`);
      toast.success('Deadline marked as completed');
      fetchDeadlines();
    } catch (error) {
      console.error('Failed to complete deadline:', error);
      toast.error('Failed to complete deadline');
    } finally {
      setCompletingId(null);
    }
  };

  // For deal-specific view
  if (dealId) {
    return (
      <Card className="bg-[#1a1a2e] border-white/10">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2 text-white">
              <Clock className="w-5 h-5 text-amber-400" />
              Deal Deadlines
            </CardTitle>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={fetchDeadlines}
              className="text-gray-400 hover:text-white"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {dealDeadlines.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No deadlines set for this deal</p>
            </div>
          ) : (
            <div className="space-y-3">
              {dealDeadlines.map((deadline) => (
                <DeadlineCard 
                  key={deadline.deadline_id}
                  deadline={deadline}
                  onComplete={handleComplete}
                  isCompleting={completingId === deadline.deadline_id}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Admin dashboard view
  const displayedDeadlines = activeView === 'overdue' ? overdueDeadlines : upcomingDeadlines;

  if (compact) {
    // Compact view for dashboard cards
    return (
      <Card className="bg-[#1a1a2e] border-white/10 h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <Clock className="w-4 h-4 text-amber-400" />
              Deadline Governance
            </CardTitle>
            <div className="flex items-center gap-1">
              {summary?.overdue_count > 0 && (
                <Badge className="bg-red-600/20 text-red-400 text-xs">
                  {summary.overdue_count} overdue
                </Badge>
              )}
              <Button 
                size="icon" 
                variant="ghost" 
                onClick={fetchDeadlines}
                className="h-6 w-6 text-gray-400"
              >
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center p-2 bg-white/5 rounded">
              <div className="text-xl font-bold text-blue-400">{summary?.total_active || 0}</div>
              <div className="text-xs text-gray-500">Active</div>
            </div>
            <div className="text-center p-2 bg-white/5 rounded">
              <div className="text-xl font-bold text-red-400">{summary?.overdue_count || 0}</div>
              <div className="text-xs text-gray-500">Overdue</div>
            </div>
            <div className="text-center p-2 bg-white/5 rounded">
              <div className="text-xl font-bold text-orange-400">{summary?.urgent_count || 0}</div>
              <div className="text-xs text-gray-500">Urgent</div>
            </div>
          </div>
          
          {overdueDeadlines.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs text-gray-400 font-medium">Critical Attention:</div>
              {overdueDeadlines.slice(0, 3).map((deadline) => (
                <div 
                  key={deadline.deadline_id}
                  className="p-2 bg-red-900/10 border border-red-600/20 rounded text-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white truncate flex-1">{deadline.description}</span>
                    <Badge className="bg-red-600/30 text-red-300 text-xs ml-2">
                      {Math.abs(deadline.days_remaining)}d overdue
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full admin view
  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <DeadlineSummaryStats summary={summary} loading={loading} />
      
      <Separator className="bg-white/10" />
      
      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={activeView === 'upcoming' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveView('upcoming')}
            className={activeView === 'upcoming' ? 'bg-amber-600' : 'bg-white/5 border-white/10'}
          >
            <Clock className="w-4 h-4 mr-2" />
            Upcoming ({upcomingDeadlines.length})
          </Button>
          <Button
            variant={activeView === 'overdue' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveView('overdue')}
            className={activeView === 'overdue' ? 'bg-red-600' : 'bg-white/5 border-white/10'}
          >
            <AlertCircle className="w-4 h-4 mr-2" />
            Overdue ({overdueDeadlines.length})
          </Button>
        </div>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={fetchDeadlines}
          className="bg-white/5 border-white/10"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Deadlines List */}
      <ScrollArea className="h-[500px]">
        {displayedDeadlines.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Clock className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg">
              {activeView === 'overdue' 
                ? 'No overdue deadlines' 
                : 'No upcoming deadlines'}
            </p>
            <p className="text-sm mt-2">
              All deadlines are being managed on time
            </p>
          </div>
        ) : (
          <div className="space-y-3 pr-4">
            {displayedDeadlines.map((deadline) => (
              <DeadlineCard 
                key={deadline.deadline_id}
                deadline={deadline}
                onComplete={handleComplete}
                isCompleting={completingId === deadline.deadline_id}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default DeadlinesMonitor;
